/**
 * InlineDdlPackageBody — the embeddable content (parked-statement scan +
 * `PackageEditFormBody`) for Oracle CREATE/ALTER PACKAGE, consumed directly
 * by the Compact DDL editor's `compactDdlBodyArm`.
 *
 * Mode selection (`create` vs `alter`) is driven by the parked statement's
 * leading keyword.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI
 * (oracle packages variant).
 */
import { useMemo } from 'react';

import type { VqeDdlEditContext } from '../edit/editContext';
import {
  PackageEditFormBody,
  type PackageFormValues,
} from '../render/common/PackageEditFormBody';
import {
  parseParkedPackageStatement,
  type ParsedParkedPackage,
} from './parseParkedPackageStatement';

function valuesFromParsed(parsed: ParsedParkedPackage): Partial<PackageFormValues> {
  return {
    kind: parsed.kind,
    name: parsed.name.name,
    ...(parsed.bodyText ? { bodyText: parsed.bodyText } : {}),
    ...(parsed.orReplace ? { orReplace: true } : {}),
    ...(parsed.oracleEditionable ? { oracleEditionable: true } : {}),
    ...(parsed.oracleNonEditionable ? { oracleNonEditionable: true } : {}),
  };
}

export interface InlineDdlPackageBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly parsedOverride?: ParsedParkedPackage | null;
  /** Invoked after a successful commit or cancel, so an embedding popover can close. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

export function InlineDdlPackageBody({
  editContext,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlPackageBodyProps) {
  const parsed = useMemo<ParsedParkedPackage | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      const text = buf.parsedDocument.source.slice(
        stmt.range.startOffset,
        stmt.range.endOffset,
      );
      return parseParkedPackageStatement(text);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);

  const mode: 'create' | 'alter' = parsed?.action === 'alter' ? 'alter' : 'create';
  const initialValues = parsed ? valuesFromParsed(parsed) : undefined;

  return (
    <PackageEditFormBody
      editContext={editContext}
      mode={mode}
      initialValues={initialValues}
      testId="inline-ddl-package-form"
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
